import { IconButton } from '@design-system/components/button/iconButton';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { ChallengeVM } from '../../view-model/Challenges.viewModel';
import {
  faArrowUpRightFromSquare,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { TMap } from '@client/new/helpers/types';
import { Pill } from '@client/new/pages/promotions/view/components/Pill';
import { Link } from 'react-router';

import * as styles from './ChallengeElement.css';
import { color } from '@design-system/tokens';
import { TimeStatus } from '../../view-model/Challenges.mapper';
import { textColorsVars } from '@design-system/vars.css';

type ChallengeElementProps = ChallengeVM;

const challengePartStatusLabels: TMap<TimeStatus, string> = {
  Finished: 'CLOSED',
  Ongoing: 'OPEN',
  Scheduled: 'N/A',
};

const challengePartStatusColors: TMap<TimeStatus, keyof typeof textColorsVars> =
  {
    Finished: 'N-60',
    Ongoing: 'SUCCESS-100',
    Scheduled: 'N-100',
  };

export const challengeStatusColors: TMap<TimeStatus, keyof typeof color> = {
  Finished: 'N-20',
  Ongoing: 'G-40',
  Scheduled: 'P-20',
};

export const ChallengePartStatus = (p: { status: TimeStatus }) => (
  <Typography
    variant="body"
    size="medium"
    fontWeight="medium"
    color={challengePartStatusColors[p.status]}
  >
    {challengePartStatusLabels[p.status]}
  </Typography>
);

export const ChallengeStatus = (p: { status: TimeStatus }) => (
  <Pill
    type="Active"
    content={p.status}
    backgroundColor={challengeStatusColors[p.status]}
  />
);

const ChallengeInfo = (
  p: { name: string } & ChallengeElementProps['registration'],
) => (
  <Col style={{ minWidth: 150 }}>
    <Row>
      <Typography variant="body" size="medium" fontWeight="normal">
        {p.name}:
      </Typography>
      <ChallengePartStatus status={p.status} />
    </Row>
    <Row>
      <Typography variant="body" size="small" fontWeight="normal" color="N-60">
        {p.desc}
      </Typography>
    </Row>
  </Col>
);

export const ChallengeElement = (p: ChallengeElementProps) => {
  return (
    <Col className={styles.wrapper}>
      <Row gap="xl">
        <Col>
          <Row
            style={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Col style={{ width: 'auto' }}>
              <Typography variant="title" size="large">
                {p.name}
              </Typography>
            </Col>
            <Col
              style={{
                margin: '0 auto',
                width: 'auto',
              }}
            >
              <ChallengeStatus status={p.status} />
            </Col>
            <Col style={{ width: 'auto' }}>
              <Row gap="l">
                <ChallengeInfo name="Registration" {...p.registration} />
                <ChallengeInfo name="COMPLETION" {...p.completion} />
              </Row>
            </Col>
          </Row>
        </Col>
        <Col style={{ width: 300, alignItems: 'flex-end' }}>
          <Row gap="m">
            {p.onShowSettings ? (
              <IconButton
                variant="filled"
                style={{ width: 190 }}
                color="primaryTonal"
                onClick={p.onShowSettings}
              >
                Settings
              </IconButton>
            ) : null}
            {p.onEdit ? (
              <IconButton
                variant="filled"
                style={{ width: 70 }}
                color="primaryTonal"
                onClick={p.onEdit}
              >
                Edit
              </IconButton>
            ) : null}
            {p.onNewChallenge ? (
              <IconButton
                variant="filled"
                style={{ width: 180 }}
                color="primaryTonal"
                onClick={p.onNewChallenge}
                icon={faPlus}
              >
                New challenge
              </IconButton>
            ) : null}
          </Row>
        </Col>
      </Row>
      <Row className={styles.lineRow} />
      <Row>
        <Col>
          <Row>
            <Typography variant="title" size="large">
              Submission dashboard
            </Typography>
          </Row>
          <Row>
            <Typography variant="body" size="medium" fontWeight="normal">
              See users progress and challenge registration & submission forms
            </Typography>
          </Row>
        </Col>
        <Col style={{ width: 190 }}>
          <Link to={p.submissionURL}>
            <IconButton
              variant="filled"
              style={{ width: 190, position: 'relative' }}
              color="primaryTonal"
              icon={faArrowUpRightFromSquare}
            >
              View submissions{' '}
              <span className={styles.submissionBubble}>
                {p.submissionsCount}
              </span>
            </IconButton>
          </Link>
        </Col>
      </Row>
    </Col>
  );
};
